import {
  type HTMLAttributeAnchorTarget,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
  Children,
  type PointerEvent,
} from 'react'
import cx from 'classnames'
import type { Observable } from 'rxjs'
import {
  Header,
  Menu,
  MenuItem,
  MenuTrigger,
  OverlayTriggerStateContext,
  PopoverContext,
  MenuSection,
  useContextProps,
  useSlottedContext,
  Separator,
  type Selection,
} from 'react-aria-components'
import styles from './Dropdown.module.css'
import { ModalOverlayVariantNames } from '@/components/ModalOverlay'
import { Icon, type IconName } from '@/components/Icon'
import {
  type AriaPopoverProps,
  type Placement,
  DismissButton,
  Overlay,
  usePopover,
} from 'react-aria'
import { mediaQueryMatchStream } from '@/utils/browser-util'
import { useObservable, useObservableValue, useSubject } from '@/hooks/rx-hooks'
import {
  type Variants,
  AnimatePresence,
  useTransform,
  LazyMotion,
  m,
} from 'motion/react'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { popoverSlideSpring } from '@/consts/springs'
import { isNotNil } from '@/utils/object-util'
import { Checkbox } from '@/components/inputs/Checkbox'
import { ModalDialog } from '@/components/ModalDialog'
import { useScrollFadeMaskImage } from '@/hooks/scroll-fade'
import { useModalAnimationStateControls } from '@/contexts/animation-state-context'

const MotionMenu = m.create(Menu)

export interface DropdownProps {
  children?: ReactNode
  /**
   * The element that triggers the dropdown.
   * This should be a `Button`.
   */
  trigger: ReactElement

  /**
   * The dropdown title.
   */
  title: string

  /**
   * Optional. Dropdown description.
   */
  description?: string

  /**
   * Whether the dropdown is open.
   * If not specified, the dropdown will control its own state.
   */
  isOpen?: boolean

  /**
   * Always render the trigger element even if there are no items in the dropdown.
   */
  alwaysRenderTrigger?: boolean

  /**
   * Where to place the dropdown.
   */
  placement?: Placement

  /**
   * Offset for the dropdown.
   */
  offset?: number

  /**
   * When specifying {@link isOpen}, will be called when the component
   * wants to change the opened state.
   *
   * @param isOpen
   */
  onOpenChange?: (isOpen: boolean) => void

  /**
   * If {@link DropdownProps.selectionMode} is set, controls which
   * items are selected (based on the item's `id` prop).
   */
  selectedKeys?: Set<string>

  /**
   * Whether selection is enabled, and if so, which mode.
   */
  selectionMode?: SelectionMode

  /**
   * Called when the selection is changed, if using selection mode.
   */
  onSelectionChange?: SelectionChangeHandler

  /**
   * Observable to trigger opening the dropdown.
   */
  open$?: Observable<unknown>
}

// Smaller breakpoint for dropdown.
const renderInPopover$ = mediaQueryMatchStream(
  'screen and (min-width: 600px) and (min-height: 485px)',
)

const loadMotionFeatures = () =>
  import('@/infra/motion-features').then((r) => r.default)

/**
 * Dropdown root element.
 */
export function Dropdown({
  trigger,
  onOpenChange,
  title,
  description,
  children,
  alwaysRenderTrigger,
  selectedKeys,
  selectionMode,
  onSelectionChange,
  isOpen: propIsOpen,
  placement = 'bottom right',
  offset,
  open$,
}: DropdownProps) {
  const headingId = useId()
  const renderInPopover = useObservableValue(renderInPopover$)
  const [internalIsOpen, setInternalIsOpen] = useState(false)

  const isOpen = propIsOpen ?? internalIsOpen
  const startDrag$ = useSubject<PointerEvent>()
  const startDrag = useCallback(
    (evt: PointerEvent) => startDrag$.next(evt),
    [startDrag$],
  )

  const setIsOpen = useCallback(
    (open: boolean) => {
      onOpenChange?.(open)
      setInternalIsOpen(open)
    },
    [onOpenChange],
  )

  const close = useCallback(() => setIsOpen(false), [setIsOpen])

  useObservable(open$, () => setIsOpen(true))

  return (
    <LazyMotion features={loadMotionFeatures}>
      {(alwaysRenderTrigger || Children.toArray(children).some(isNotNil)) && (
        <MenuTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
          {trigger}
          {renderInPopover ? (
            <AnimatePresence>
              {isOpen && (
                <Popover
                  placement={placement}
                  offset={offset}
                  containerPadding={48}
                >
                  <DropdownMenu
                    isOpen={isOpen}
                    headingId={headingId}
                    title={title}
                    description={description}
                    selectedKeys={selectedKeys}
                    selectionMode={selectionMode}
                    onSelectionChange={onSelectionChange}
                    isPopover={true}
                  >
                    {children}
                  </DropdownMenu>
                </Popover>
              )}
            </AnimatePresence>
          ) : (
            <ModalDialog
              isDismissable
              isOpen={isOpen}
              onClose={close}
              aria-labelledby={headingId}
              startDrag$={startDrag$}
            >
              <DropdownMenu
                isOpen={isOpen}
                headingId={headingId}
                title={title}
                description={description}
                selectedKeys={selectedKeys}
                selectionMode={selectionMode}
                onSelectionChange={onSelectionChange}
                onStartDrag={startDrag}
                isPopover={false}
              >
                {children}
              </DropdownMenu>
            </ModalDialog>
          )}
        </MenuTrigger>
      )}
    </LazyMotion>
  )
}

interface PopoverProps
  extends Omit<AriaPopoverProps, 'popoverRef' | 'triggerRef'> {
  children?: ReactNode
}

/**
 * Popover, used for rendering the menu near the trigger on larger screens.
 */
function Popover(props: PopoverProps) {
  ;[props] = useContextProps(props, null, PopoverContext)

  // The popover context contains the trigger ref and is set
  // by the menu trigger.
  const popoverContextProps = useSlottedContext(PopoverContext)!

  // Provided by the menu's overlay.
  const state = useContext(OverlayTriggerStateContext)!
  const popoverRef = useRef<HTMLDivElement>(null)
  const { popoverProps, underlayProps } = usePopover(
    {
      offset: 4,
      ...props,
      popoverRef,
      containerPadding: 36,
      triggerRef: popoverContextProps.triggerRef!,
    },
    state,
  )

  // Animation variants.
  const variants = useMemo(
    (): Variants => ({
      [ModalOverlayVariantNames.VISIBLE]: {
        y: 0,
        opacity: 1,
      },
      [ModalOverlayVariantNames.HIDDEN]: () => ({
        y: 10,
        opacity: 0,
      }),
    }),
    [],
  )

  const { isExiting, ...animationStateProps } = useModalAnimationStateControls({
    isOpen: state.isOpen,
    visibleVariant: ModalOverlayVariantNames.VISIBLE,
    hiddenVariant: ModalOverlayVariantNames.HIDDEN,
  })

  return (
    <Overlay isExiting={isExiting}>
      {!props.isNonModal && (
        <div className={styles.underlay} {...underlayProps} />
      )}
      {/* @ts-ignore: some typing conflict between aria and motion */}
      <m.div
        variants={variants}
        {...animationStateProps}
        transition={{
          type: 'spring',
          ...popoverSlideSpring,
        }}
        {...popoverProps}
        ref={popoverRef}
        className={styles.popover}
      >
        {
          // The double-dismiss button is based on the official component
          !props.isNonModal && <DismissButton onDismiss={state.close} />
        }
        {props.children}
        <DismissButton onDismiss={state.close} />
      </m.div>
    </Overlay>
  )
}

/**
 * Dropdown selection mode
 */
export type SelectionMode = 'single' | 'multiple'

/**
 * Handler signature for selection changes.
 */
export type SelectionChangeHandler = (selection: 'all' | Set<string>) => unknown

interface DropdownMenuProps {
  children?: ReactNode
  title: string
  description?: string
  headingId: string
  isOpen: boolean
  selectedKeys?: Set<string>
  selectionMode?: SelectionMode
  onSelectionChange?: SelectionChangeHandler
  onStartDrag?: (event: PointerEvent) => void
  isPopover?: boolean
}

/**
 * The actual dropdown menu.
 */
function DropdownMenu({
  title,
  description,
  headingId,
  children,
  selectedKeys,
  selectionMode,
  onSelectionChange,
  onStartDrag,
  isPopover,
}: DropdownMenuProps) {
  const {
    connectRef: connectMenuRef,
    scrollYStart,
    scrollYEnd,
  } = useScrollOffsets()
  const { maskImage } = useScrollFadeMaskImage({
    scrollYStart,
    scrollYEnd,
    fade: 'bottom',
    fadeSegmentHeight: 'var(--spacing-5xl)',
  })
  const headerBorderOpacity = useTransform(scrollYStart, [0, 20], [0, 1])

  return (
    <div className={cx(styles.menuContainer, !isPopover && styles.dialog)}>
      <MotionMenu
        aria-labelledby={headingId}
        className={styles.menu}
        selectedKeys={selectedKeys}
        selectionMode={selectionMode}
        ref={connectMenuRef}
        style={{ maskImage }}
        onSelectionChange={
          // This cast is fine because we only allow string keys
          onSelectionChange as (selection: Selection) => unknown
        }
      >
        <MenuSection>
          <Header className={styles.header} onPointerDown={onStartDrag}>
            <div className={styles.headerContent}>
              <h1 id={headingId} className={styles.title}>
                {title}
              </h1>
              {description && (
                <p className={styles.description}>{description}</p>
              )}
            </div>
            <m.div
              aria-hidden
              className={styles.headerBorder}
              style={{ opacity: headerBorderOpacity }}
            />
          </Header>
          {children}
        </MenuSection>
      </MotionMenu>
    </div>
  )
}

interface DropdownItemProps {
  children?: ReactNode
  /**
   * Icon to display on the left side of the content.
   */
  icon?: IconName
  /**
   * Called when the user interacts with the item.
   */
  onAction?: () => void
  /**
   * Whether the item is disabled.
   */
  isDisabled?: boolean
  /**
   * If set, the item becomes a link.
   */
  href?: string
  /**
   * Text value to render for accessibility purposes.
   */
  textValue?: string
  /**
   * If {@link DropdownItemProps.href} is set, defines the target.
   */
  target?: HTMLAttributeAnchorTarget
  /**
   * If using selection, defines the item's key.
   */
  id?: string
  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Item in the dropdown.
 */
export function DropdownItem({
  href,
  onAction,
  children,
  isDisabled,
  icon,
  target,
  textValue,
  id,
  testId,
}: DropdownItemProps) {
  return (
    <MenuItem
      isDisabled={isDisabled}
      target={target}
      onAction={onAction}
      href={href}
      id={id}
      data-test={testId}
      textValue={
        textValue ?? (typeof children === 'string' ? children : undefined)
      }
      className={({ isFocused, isPressed, isFocusVisible, isSelected }) =>
        cx(
          styles.item,
          isPressed && styles.pressed,
          isFocused && styles.focus,
          isFocusVisible && styles.focusVisible,
          isDisabled && styles.disabled,
          isSelected && styles.selected,
        )
      }
    >
      {({ isSelected, selectionMode }) => (
        <>
          {icon && (
            <span className={styles.icon}>
              <Icon name={icon} variant="inherit" />
            </span>
          )}
          <div className={cx(styles.itemContent)}>{children}</div>
          {selectionMode === 'single' ? (
            isSelected && (
              <span className={styles.icon}>
                <Icon name="tick" variant="active" />
              </span>
            )
          ) : selectionMode === 'multiple' ? (
            <Checkbox inert isPadded checked={isSelected} />
          ) : null}
        </>
      )}
    </MenuItem>
  )
}

interface DropdownItemTitleProps {
  children?: ReactNode
  testId?: string
}

/**
 * Dropdown Item Title
 */
export function DropdownItemTitle({
  children,
  testId,
}: DropdownItemTitleProps) {
  return (
    <p data-test={testId} className={styles.itemTitle}>
      {children}
    </p>
  )
}

interface DropdownItemDescriptionProps {
  children?: ReactNode
  testId?: string
}

/**
 * Dropdown Item Description
 */
export function DropdownItemDescription({
  children,
  testId,
}: DropdownItemDescriptionProps) {
  return (
    <div data-test={testId} className={styles.itemDescription}>
      {children}
    </div>
  )
}

interface DropdownItemSeparatorProps {
  testId?: string
}

/**
 * Dropdown Item Separator
 */
export function DropdownItemSeparator({ testId }: DropdownItemSeparatorProps) {
  return <Separator data-test={testId} className={styles.itemSeparator} />
}
